import { ProvidedBy } from '../../../common/types'
import { SavingsDepositTxOverview } from '../../types'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { SparkRewardPill } from '@/ui/molecules/spark-reward-pill/SparkRewardPill'
import { TransactionOverview } from '@/ui/organisms/transaction-overview/TransactionOverview'
import { testIds } from '@/ui/utils/testIds'

interface SavingsDepositTransactionOverviewProps {
  txOverview: SavingsDepositTxOverview
}

function SavingsDepositTransactionOverview({ txOverview }: SavingsDepositTransactionOverviewProps) {
  const showExtraRewards = txOverview.relatedCampaigns.length > 0

  if (txOverview.status !== 'success') {
    return (
      <TransactionOverviewPlaceholder
        providedBy={txOverview.providedBy}
        savingsToken={txOverview.savingsToken}
        showExtraRewards={showExtraRewards}
      />
    )
  }
  const {
    APY,
    stableEarnRate,
    underlyingToken,
    underlyingTokenToDeposit,
    underlyingTokenDepositedBefore,
    underlyingTokenDepositedAfter,
    accountName,
  } = txOverview

  return (
    <TransactionOverview providedBy={txOverview.providedBy}>
      <TransactionOverview.Row>
        <TransactionOverview.Label>Current APY</TransactionOverview.Label>
        <TransactionOverview.SavingsApy apy={APY} stableEarnRate={stableEarnRate} underlyingToken={underlyingToken} />
      </TransactionOverview.Row>

      {txOverview.savingsToken.symbol !== TokenSymbol('stUSDS') && (
        <TransactionOverview.Row>
          <TransactionOverview.Label>Savings Balance</TransactionOverview.Label>
          <TransactionOverview.SavingsBalanceChange
            token={underlyingToken}
            amountBefore={underlyingTokenDepositedBefore}
            amountAfter={underlyingTokenDepositedAfter}
          />
        </TransactionOverview.Row>
      )}
      <TransactionOverview.Row>
        <TransactionOverview.Label>Outcome</TransactionOverview.Label>
        <TransactionOverview.SavingsOutcome
          token={underlyingToken}
          amount={underlyingTokenToDeposit}
          postFix={`Deposited into ${accountName}`}
          dataTestId={testIds.dialog.savings.transactionOverview.outcome}
        />
      </TransactionOverview.Row>
      {txOverview.relatedCampaigns.length > 0 && (
        <TransactionOverview.Row>
          <TransactionOverview.Label>Extra Rewards</TransactionOverview.Label>
          <TransactionOverview.Generic>
            {txOverview.relatedCampaigns.map((campaign) => (
              <SparkRewardPill key={campaign.campaignId} {...campaign} />
            ))}
          </TransactionOverview.Generic>
        </TransactionOverview.Row>
      )}
    </TransactionOverview>
  )
}

function TransactionOverviewPlaceholder({
  providedBy,
  showExtraRewards,
  savingsToken,
}: {
  providedBy: ProvidedBy
  showExtraRewards: boolean
  savingsToken: Token
}) {
  const placeholder = '-'
  return (
    <TransactionOverview providedBy={providedBy}>
      <TransactionOverview.Row>
        <TransactionOverview.Label>Current APY</TransactionOverview.Label>
        <TransactionOverview.Generic>{placeholder}</TransactionOverview.Generic>
      </TransactionOverview.Row>
      {savingsToken.symbol !== TokenSymbol('stUSDS') && (
        <TransactionOverview.Row>
          <TransactionOverview.Label>Savings Balance</TransactionOverview.Label>
          <TransactionOverview.Generic>{placeholder}</TransactionOverview.Generic>
        </TransactionOverview.Row>
      )}
      <TransactionOverview.Row>
        <TransactionOverview.Label>Outcome</TransactionOverview.Label>
        <TransactionOverview.Generic>{placeholder}</TransactionOverview.Generic>
      </TransactionOverview.Row>
      {showExtraRewards && (
        <TransactionOverview.Row>
          <TransactionOverview.Label>Extra Rewards</TransactionOverview.Label>
          <TransactionOverview.Generic>{placeholder}</TransactionOverview.Generic>
        </TransactionOverview.Row>
      )}
    </TransactionOverview>
  )
}

export {
  SavingsDepositTransactionOverview as TransactionOverview,
  type SavingsDepositTransactionOverviewProps as TransactionOverviewProps,
}
